import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Card, CardContent, Typography, Box, Tabs, Tab, Select, MenuItem } from '@mui/material';
import { DateRange, Assessment, TrendingUp, People, Speed, Public, Visibility } from '@mui/icons-material';
import styles from './Analytics.module.css';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import { useNavigate } from 'react-router-dom';
import { checkAuthenticatedAdmin } from '../utils/client-functions';
import { useErrorSetter } from '../hooks/useUiMessages';
import axios from 'axios'; // Commented out until real endpoints are implemented
import { subDays, startOfDay, endOfDay } from 'date-fns';

// Import tab components (will create these next)
import AnalyticsOverview from './AnalyticsOverview';
import ConversionFunnel from './ConversionFunnel';
import UserEngagement from './UserEngagement';
import PerformanceMonitor from './PerformanceMonitor';
import GeographicInsights from './GeographicInsights';
import RealtimeMonitor from './RealtimeMonitor';

const AnalyticsDashboard = () => {
    const navigate = useNavigate();
    const setError = useErrorSetter();
    const [currentTab, setCurrentTab] = useState(0);
    // Date range preset selector: 7, 30, 90 days
    const [rangePreset, setRangePreset] = useState(30);
    const [dateRange, setDateRange] = useState({
        start: startOfDay(subDays(new Date(), 30)),
        end: endOfDay(new Date())
    });
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const isAdminAuth = sessionStorage?.getItem("afd8TvhsdjwiuuvsgjhsAfgsUhjs"); // Commented out until real endpoints are used

    // Tab configuration
    const tabs = [
        { label: 'Overview', icon: <Assessment />, component: AnalyticsOverview },
        { label: 'Conversion', icon: <TrendingUp />, component: ConversionFunnel },
        { label: 'Engagement', icon: <People />, component: UserEngagement },
        { label: 'Performance', icon: <Speed />, component: PerformanceMonitor },
        { label: 'Geographic', icon: <Public />, component: GeographicInsights },
        { label: 'Real-time', icon: <Visibility />, component: RealtimeMonitor }
    ];

    // Handle preset change
    const handlePresetChange = (e) => {
        const days = Number(e.target.value);
        setRangePreset(days);
        const start = startOfDay(subDays(new Date(), days));
        const end = endOfDay(new Date());
        const newRange = { start, end };
        setDateRange(newRange);
        fetchAnalyticsData(newRange);
    };

    // Fetch analytics data
    const fetchAnalyticsData = useCallback(async (overrideRange) => {
        try {
            //must await
            await checkAuthenticatedAdmin()
        } catch (error) {
            return navigate("/");      
        }
        setLoading(true);
        
        try {
            const range = overrideRange || dateRange;
            const startDate = range.start.toISOString();
            const endDate = range.end.toISOString();

            // Compute previous period matching current window length
            const windowDays = Math.max(1, Math.round((range.end - range.start) / (24 * 60 * 60 * 1000)));
            const prevStartDate = startOfDay(subDays(range.start, windowDays)).toISOString();
            const prevEndDate = endOfDay(subDays(range.start, 1)).toISOString();
            
            // Uncomment below when analytics endpoints are implemented:
        
            const [summaryRes, conversionRes, engagementRes, prevSummaryRes, prevConversionRes] = await Promise.all([
                axios.get(`/origin/analytics/summary?startDate=${startDate}&endDate=${endDate}`, {
                    headers: { "x-access-token": isAdminAuth }
                }),
                axios.get(`/origin/analytics/conversion?startDate=${startDate}&endDate=${endDate}`, {
                    headers: { "x-access-token": isAdminAuth }
                }),
                axios.get(`/origin/analytics/engagement?startDate=${startDate}&endDate=${endDate}`, {
                    headers: { "x-access-token": isAdminAuth }
                }),
                axios.get(`/origin/analytics/summary?startDate=${prevStartDate}&endDate=${prevEndDate}`, {
                    headers: { "x-access-token": isAdminAuth }
                }),
                axios.get(`/origin/analytics/conversion?startDate=${prevStartDate}&endDate=${prevEndDate}`, {
                    headers: { "x-access-token": isAdminAuth }
                })
            ]);

            // Derive KPI deltas vs previous period
            const safeNum = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);
            const currSessions = safeNum(summaryRes.data?.summary?.totalSessions);
            const prevSessions = safeNum(prevSummaryRes.data?.summary?.totalSessions);
            const currAuth = safeNum(summaryRes.data?.summary?.authUsers);
            const currUnauth = safeNum(summaryRes.data?.summary?.unauthUsers);
            const prevAuth = safeNum(prevSummaryRes.data?.summary?.authUsers);
            const prevUnauth = safeNum(prevSummaryRes.data?.summary?.unauthUsers);
            const currActiveUsers = currAuth + currUnauth;
            const prevActiveUsers = prevAuth + prevUnauth;
            const currEvents = safeNum(summaryRes.data?.summary?.totalAnalyticsEvents);
            const prevEvents = safeNum(prevSummaryRes.data?.summary?.totalAnalyticsEvents);
            const currConvRate = safeNum(conversionRes.data?.conversionMetrics?.subscriptionRate);
            const prevConvRate = safeNum(prevConversionRes.data?.conversionMetrics?.subscriptionRate);

            const pctChange = (curr, prev) => {
                if (prev === 0) return curr > 0 ? 100 : 0; // treat as 100% growth from zero
                return ((curr - prev) / prev) * 100;
            };

            const kpiChange = {
                sessionsPct: pctChange(currSessions, prevSessions),
                // Conversion rate delta in percentage points
                conversionDeltaPoints: currConvRate - prevConvRate,
                activeUsersPct: pctChange(currActiveUsers, prevActiveUsers),
                eventsPct: pctChange(currEvents, prevEvents)
            };

            setAnalyticsData({
                summary: summaryRes.data,
                conversion: conversionRes.data,
                engagement: engagementRes.data,
                kpiChange,
                dateRange: { startDate, endDate }
            });

        } catch (error) {
            console.error('Analytics fetch error:', error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                navigate('/');
            } else {
                setError('Failed to fetch analytics data');
            }
        } finally {
            setLoading(false);
        }
    }, [dateRange, navigate, setError, isAdminAuth]);

    useEffect(() => {
        // Initial fetch on mount using default 30-day range
        fetchAnalyticsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    // No manual fetch button needed; changes auto-trigger.

    // Render current tab component
    const renderTabContent = () => {
    const TabComponent = tabs[currentTab].component;
        
        return (
            <TabComponent 
                data={analyticsData} 
                loading={loading} 
                dateRange={{ start: dateRange.start, end: dateRange.end }}
                onRefresh={fetchAnalyticsData}
            />
        );
    };

    return (
        <div className={styles.analyticsContainer}>
            <AdminSideMenu />
            
            <div className={styles.analyticsContent}>
                {/* Header */}
                <Box className={styles.analyticsHeader}>
                    <Box className={styles.headerLeft}>
                        <Typography variant="h4" className={styles.pageTitle}>
                            Analytics Dashboard
                        </Typography>
                        <Typography variant="subtitle1" className={styles.pageSubtitle}>
                            Comprehensive insights into user behavior and system performance
                        </Typography>
                    </Box>
                        <Box className={styles.headerRight}>
                            <Select
                                value={rangePreset}
                                onChange={handlePresetChange}
                                size="small"
                                className={styles.dateRangeSelect}
                                sx={{ backgroundColor: '#fff' }}
                            >
                                <MenuItem value={7}>Last 7 days</MenuItem>
                                <MenuItem value={30}>Last 30 days</MenuItem>
                                <MenuItem value={90}>Last 90 days</MenuItem>
                            </Select>
                        </Box>
                </Box>

                {/* KPI Summary Cards */}
                {analyticsData && (
                    <Grid container spacing={3} className={styles.kpiContainer}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className={styles.kpiCard}>
                                <CardContent>
                                    <Typography className={styles.kpiLabel}>
                                        Total Sessions
                                    </Typography>
                                    <Typography className={styles.kpiValue}>
                                        {analyticsData.summary?.summary?.totalSessions || 0}
                                    </Typography>
                                    <Typography className={styles.kpiChange}>
                                        {analyticsData?.kpiChange ? `${analyticsData.kpiChange.sessionsPct >= 0 ? '+' : ''}${(Math.abs(analyticsData.kpiChange.sessionsPct)).toFixed(1)}% from last period` : ''}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className={styles.kpiCard}>
                                <CardContent>
                                    <Typography className={styles.kpiLabel}>
                                        Conversion Rate
                                    </Typography>
                                    <Typography className={styles.kpiValue}>
                                        {analyticsData.conversion?.conversionMetrics?.subscriptionRate?.toFixed(1) || 0}%
                                    </Typography>
                                    <Typography className={styles.kpiChange}>
                                        {analyticsData?.kpiChange ? `${analyticsData.kpiChange.conversionDeltaPoints >= 0 ? '+' : ''}${Math.abs(analyticsData.kpiChange.conversionDeltaPoints).toFixed(1)} pp from last period` : ''}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className={styles.kpiCard}>
                                <CardContent>
                                    <Typography className={styles.kpiLabel}>
                                        Active Users
                                    </Typography>
                                    <Typography className={styles.kpiValue}>
                                        {analyticsData.summary?.summary?.authUsers + analyticsData.summary?.summary?.unauthUsers || 0}
                                    </Typography>
                                    <Typography className={styles.kpiChange}>
                                        {analyticsData?.kpiChange ? `${analyticsData.kpiChange.activeUsersPct >= 0 ? '+' : ''}${Math.abs(analyticsData.kpiChange.activeUsersPct).toFixed(1)}% from last period` : ''}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className={styles.kpiCard}>
                                <CardContent>
                                    <Typography className={styles.kpiLabel}>
                                        Total Events
                                    </Typography>
                                    <Typography className={styles.kpiValue}>
                                        {analyticsData.summary?.summary?.totalAnalyticsEvents || 0}
                                    </Typography>
                                    <Typography className={styles.kpiChange}>
                                        {analyticsData?.kpiChange ? `${analyticsData.kpiChange.eventsPct >= 0 ? '+' : ''}${Math.abs(analyticsData.kpiChange.eventsPct).toFixed(1)}% from last period` : ''}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Tab Navigation */}
                <Box className={styles.tabsContainer}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        className={styles.tabs}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                    >
                        {tabs.map((tab, index) => (
                            <Tab
                                key={index}
                                label={
                                    <Box className={styles.tabLabel}>
                                        {tab.icon}
                                        <span className={styles.tabText}>{tab.label}</span>
                                    </Box>
                                }
                                className={styles.tab}
                            />
                        ))}
                    </Tabs>
                </Box>

                {/* Tab Content */}
                <Box className={styles.tabContent}>
                    {renderTabContent()}
                </Box>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
